import * as Ably from 'ably'
import type { ChannelEvent, ChannelStateChange, ErrorInfo, InboundMessage, TokenParams } from 'ably'
import axios from 'axios'

import { getEnv, getProxyTarget } from 'src/utils/env'
import { decrypt } from 'src/utils/decryption'
import { rollbarService } from 'src/services/RollbarService'

import { StreamHistoryOptions, StreamMessage, StreamSourceBase, StreamSourceInterface } from './StreamClient'

type AblyStreamSourceOptions = {
    url: string
    sessionId: string
    /** Empty string for original language, populated for translations (used in channel name) */
    translatedLanguageCode: string
    options: Record<string, any>
    verbose?: boolean
}

export class AblyStreamSource extends StreamSourceBase implements StreamSourceInterface {
    private client: Ably.Realtime | null = null
    private readonly sessionId: string
    private readonly translatedLanguageCode: string
    private options: Record<string, any>

    constructor({ url, sessionId, translatedLanguageCode, options, verbose }: AblyStreamSourceOptions) {
        super()

        this.sessionId = sessionId
        this.translatedLanguageCode = translatedLanguageCode
        this.options = options
        this.client = new Ably.Realtime({
            authHeaders: { 'X-Requested-With': 'XMLHttpRequest' },
            closeOnUnload: false,
            authUrl: url,
            httpRequestTimeout: 30000,
            realtimeRequestTimeout: 30000,
            authCallback: async (_tokenParams: TokenParams, callback) => {
                try {
                    const { data: tokenRequest } = await axios({ method: 'get', url, withCredentials: true })
                    callback(null, tokenRequest)
                } catch (e: unknown) {
                    callback(e as ErrorInfo, null)
                }
            },
            ...options,
            logLevel: verbose ? 4 : 0,
        })
    }

    close() {
        this.client?.connection.close()
    }

    private get channelLanguage() {
        return this.translatedLanguageCode ? this.translatedLanguageCode.toLowerCase().replace(/-/g, '_') : ''
    }
    private channel(target: string) {
        return [
            'trax',
            'session',
            getEnv() === 'dev' ? (getProxyTarget() === 'local' ? 'staging' : getProxyTarget()) : getEnv(),
            this.sessionId,
            this.channelLanguage,
            target,
        ]
            .filter((p) => !!p)
            .join(':')
    }

    private prepareMessage(m: InboundMessage) {
        try {
            let data = m.data
            if (data instanceof ArrayBuffer) {
                // Convert binary data to JSON when an Ably message is transmitted as binary, as our system consistently anticipates JSON data.
                data = JSON.parse(String.fromCharCode(...new Uint8Array(data)))
            }

            const hasEncryptedPayload = !!data?.data?.encrypted_data

            if (hasEncryptedPayload) {
                // Decrypted the encrypted data using the provided cipher key
                // Check if the cipher key exists and isn't empty, else log error and return null

                if (!this.options.cipher || !this.options.cipher.key) {
                    console.error(`[Ably-Integration] Cipher key not provided`, m)
                    rollbarService.error('[Ably-Integration] Cipher key not provided')

                    return null
                }

                const { encrypted_data } = data.data as { encrypted_data: string }
                data.data = JSON.parse(decrypt(encrypted_data, this.options.cipher.key))
            }

            return data
        } catch (e) {
            console.error(`[Ably-Integration] encountered error`, e, m)
            rollbarService.error('[Ably-Integration] encountered error preparing data')

            return null
        }
    }

    private messageListener = (m: InboundMessage) => {
        const msg = this.prepareMessage(m)

        if (msg) {
            this.send(msg)
        }
    }

    private static readonly FAILED_CHANNEL_EVENTS: ChannelEvent[] = ['failed', 'suspended']
    private connectivityFailureListener = (evt: ChannelStateChange) => {
        rollbarService.error(`[Ably-Integration] channel connection: ${evt.current}${evt.reason ? ', ' + evt.reason.message : ''}`)
    }

    subscribe(...targets: string[]) {
        targets.forEach((t) => {
            if (!this.client) {
                return
            }
            const channel = this.client.channels.get(this.channel(t))
            channel.subscribe(this.messageListener)

            AblyStreamSource.FAILED_CHANNEL_EVENTS.forEach((evt) => channel.on(evt, this.connectivityFailureListener))
        })
    }

    unsubscribe(...targets: string[]) {
        targets.forEach((t) => {
            if (!this.client) {
                return
            }
            const channel = this.client.channels.get(this.channel(t))
            channel.unsubscribe(this.messageListener)

            AblyStreamSource.FAILED_CHANNEL_EVENTS.forEach((evt) => channel.off(evt, this.connectivityFailureListener))
        })
    }

    async history<T extends StreamMessage['action']>(target: string, options: StreamHistoryOptions) {
        if (!this.client) {
            return []
        }

        const result = await this.client.channels.get(this.channel(target)).history(options)

        return result.items.map((item) => this.prepareMessage(item)).filter((m) => m !== null) as Extract<StreamMessage, { action: T }>[]
    }
}
